import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Edit3, FileText, History, Trash2 } from 'lucide-react';
import {
  activityLogService,
  type ActivityLog,
  type ActivityType,
} from '../../services/activityLogService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../features/language/useLanguage';

interface ActivityLogsPreviewProps {
  limit?: number;
}

const TYPE_BADGE: Record<ActivityType, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

const TYPE_ICON: Record<ActivityType, React.ElementType> = {
  create: FileText,
  update: Edit3,
  delete: Trash2,
};

const shortId = (id: string) => (id && id.length > 8 ? `${id.slice(0, 8)}…` : id || '—');

const formatModule = (module: string) =>
  module
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const ActivityLogsPreview: React.FC<ActivityLogsPreviewProps> = ({ limit = 5 }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['activityLogs', 'preview', { limit }],
    queryFn: () => activityLogService.list({ page: 1, size: limit }),
  });

  const logs = useMemo(() => (data?.data ?? []).slice(0, limit), [data, limit]);

  return (
    <div className="bg-white shadow sm:rounded-lg" data-test-id="activity-logs-preview-container">
      <div className="px-4 py-5 sm:p-6">
        <div className="md:flex md:items-start md:justify-between gap-4">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
              <History className="h-5 w-5 text-gray-500" />
              {t('activityLogs.title', undefined, 'Activity Logs')}
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>{t('activityLogs.description', undefined, 'Audit trail of all create, update, and delete operations across business modules.')}</p>
            </div>
          </div>
          <div className="mt-4 md:mt-0 shrink-0">
            <button
              type="button"
              onClick={() => navigate('/activity-logs')}
              data-test-id="activity-logs-view-all-button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:underline"
            >
              {t('activityLogs.viewAll', undefined, 'View all')}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <LoadingSpinner />
          ) : isError ? (
            <div className="py-6 text-center text-sm text-red-500" data-test-id="activity-logs-preview-error">
              {t('activityLogs.loadError', undefined, 'Failed to load activity logs.')}
            </div>
          ) : logs.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500" data-test-id="activity-logs-preview-empty">
              {t('activityLogs.empty', undefined, 'No activity recorded for these filters.')}
            </div>
          ) : (
            <ul
              className="divide-y divide-gray-200 border-t border-b border-gray-200"
              data-test-id="activity-logs-preview-list"
            >
              {logs.map((log) => (
                <PreviewRow key={log.id} log={log} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

function PreviewRow({ log }: { log: ActivityLog }) {
  const { t } = useLanguage();
  const Icon = TYPE_ICON[log.activity_type];
  const time = new Date(log.created_at).toLocaleString();
  const reference = log.reference_label?.trim() || shortId(log.reference_id);
  const user = log.user_name?.trim() || shortId(log.user_id);
  return (
    <li
      className="py-3 flex items-center gap-3"
      data-test-id={`activity-logs-preview-row-${log.id}`}
    >
      <span
        className={`inline-flex items-center justify-center h-8 w-8 rounded-full shrink-0 ${TYPE_BADGE[log.activity_type]}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
            {reference}
          </span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            {formatModule(log.module)}
          </span>
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${TYPE_BADGE[log.activity_type]}`}
          >
            {t(`activityLogs.types.${log.activity_type}`, undefined, log.activity_type)}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-gray-500 truncate">
          {user} · {time}
        </div>
      </div>
    </li>
  );
}
